import { useCallback, useEffect, useMemo, useState } from "react";

import type {
  LookbookItemDataFragment,
  LookbookSectionDataFragment,
} from "../graphql/types";
import type { CheckoutEnabledConfig } from "../lookbook";
import type {
  ProductDataFragment,
  ProductVariantsQuery,
} from "../shopify-graphql/types";
import { Shopify } from "../shopify-sdk";
import { formatShopifyProductId, getProductIdsFromSections } from "../utils";

export interface UseItemsWithCheckoutParams {
  checkout: CheckoutEnabledConfig;
  sections: LookbookSectionDataFragment[];
}

export interface ItemWithCheckoutProduct extends LookbookItemDataFragment {
  checkoutProduct?: ProductDataFragment;
}

export interface SectionWithCheckoutProducts
  extends LookbookSectionDataFragment {
  items: ItemWithCheckoutProduct[];
}

interface State {
  status: "idle" | "loading" | "ready" | "error";
  products: ProductDataFragment[] | null;
}

async function getAllVariants(
  shopify: Shopify,
  product: ProductDataFragment,
): Promise<ProductDataFragment> {
  if (!product.variants.pageInfo.hasNextPage) {
    return product;
  }

  let after: string | null =
    product.variants.edges[product.variants.edges.length - 1].cursor;
  const variantEdges = product.variants.edges;
  while (after !== null) {
    const res: ProductVariantsQuery = await shopify.sdk.ProductVariants({
      productId: product.id,
      first: 100,
      after,
    });
    variantEdges.push(...(res.product?.variants.edges ?? []));
    after = res.product?.variants.pageInfo.hasNextPage
      ? res.product.variants.edges[res.product.variants.edges.length - 1].cursor
      : null;
  }

  return {
    ...product,
    variants: {
      ...product.variants,
      edges: variantEdges,
    },
  };
}

export function useItemsWithCheckout(params: UseItemsWithCheckoutParams) {
  const { checkout, sections } = params;

  const [state, setState] = useState<State>({ status: "idle", products: null });

  const fetchShopifyProducts = useCallback(async () => {
    try {
      if (state.status === "loading") {
        return;
      }

      setState((prev) => ({ ...prev, status: "loading" }));

      const shopify = new Shopify(checkout.config);

      const res = await shopify.sdk.Products({
        ids: getProductIdsFromSections(sections, checkout.integrationId),
      });

      const productsWithoutAllVariants = res.nodes.flatMap((product) => {
        if (product?.__typename !== "Product") {
          return [];
        }
        return [product];
      });

      const products = await Promise.all(
        productsWithoutAllVariants.map((product) => {
          return getAllVariants(shopify, product);
        }),
      );

      setState({
        status: "ready",
        products,
      });
    } catch (e) {
      setState((prev) => ({ ...prev, status: "error" }));
    }
  }, [checkout.config, checkout.integrationId, sections, state.status]);

  useEffect(() => {
    if (state.status === "idle" && checkout.enabled) {
      fetchShopifyProducts();
    }
  }, [state, checkout, fetchShopifyProducts]);

  const sectionsWithCheckout = useMemo(() => {
    const checkoutProducts = state.products ?? [];

    return sections.map((section): SectionWithCheckoutProducts => {
      return {
        ...section,
        items: section.items.map((item): ItemWithCheckoutProduct => {
          if (item.resource?.__typename === "Product" && checkout.enabled) {
            const { resource } = item;
            return {
              ...item,
              checkoutProduct: checkoutProducts?.find(
                (p) => p.id === formatShopifyProductId(resource.external_id),
              ),
            };
          }

          return {
            ...item,
            checkoutProduct: undefined,
          };
        }),
      };
    });
  }, [sections, state.products, checkout.enabled]);

  return { sections: sectionsWithCheckout };
}
